import React, { useCallback, useState } from "react";
import { buildPublicAssetPath } from "../utils/publicAssetPath";
import { buildSrcSet } from "../utils/images";

// Responsive <img> that stays transparent until decoded and then fades in over
// whatever placeholder its container shows. Images already in the browser
// cache appear instantly, without the fade.
// Slides hidden by a carousel (display: none) are never fetched by lazy
// loading, so preloaded neighbours must pass loading="eager".
const ProgressiveImage = ({
  src,
  alt,
  sizes,
  priority = false,
  loading = "lazy",
  fetchPriority = "auto",
  className = "",
  onLoad,
  ...rest
}) => {
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [isInstant, setIsInstant] = useState(false);

  const handleLoad = useCallback(
    (event) => {
      setLoadedSrc(src);
      onLoad?.(event);
    },
    [src, onLoad],
  );

  const imageRef = useCallback(
    (node) => {
      if (node?.complete && node.naturalWidth > 0) {
        setIsInstant(true);
        setLoadedSrc(src);
        onLoad?.();
      }
    },
    [src, onLoad],
  );

  const classes = [
    className,
    "progressive-image",
    loadedSrc === src && "is-loaded",
    isInstant && "is-instant",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <img
      {...rest}
      ref={imageRef}
      alt={alt}
      className={classes}
      // loading/sizes go before src so no browser starts an eager full-size fetch
      loading={priority ? "eager" : loading}
      fetchpriority={priority ? "high" : fetchPriority}
      decoding="async"
      sizes={sizes}
      srcSet={buildSrcSet(src)}
      src={buildPublicAssetPath(src)}
      onLoad={handleLoad}
      onError={handleLoad}
    />
  );
};

export default ProgressiveImage;
